import { Alert, Card, Col, Row, Space, Spin, Tabs } from 'antd';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router';
import { TimeZoneQueries } from '../../queries/time-zone-queries';
import { CompanyQueries } from '../../queries/company-queries';
import { ProductQueries } from '../../queries/product-queries';
import CompanyAddressCard from './CompanyAddressCard';
import FeaturesCard from './FeaturesCard';
import { ProductsTab } from './ProductsTab';
import { CompanyFeaturesTab } from './CompanyFeaturesTab';
import { UserQueries } from '../../queries/user-queries';
import UsersTab from './UsersTab';
import { HasRight, Rights } from '../../utilites/auth';
import { SubscriptionQueries } from '../../queries/subscription-queries';
import CompanyBillingAddressCard from './CompanyBillingAddressCard';
import CompanyAlert from './CompanyAlert';
import { ActivityLogQueries } from '../../queries/activity-log-queries';
import ActivityChartCard from '../../components/activity-chart/ActivityChartCard';
import { ParentChildrenCard } from './FeaturesCard/ParentChildrenCard';
import { CustomerServiceQueries } from '../../queries/customer-service-queries';
import { CompanyUsageTab } from './CompanyUsageTab';

const { TabPane } = Tabs;

interface RouteParams {
  companyId: string;
  tab: string | undefined;
}

export default function CompanyDetailPage() {
  const { companyId, tab } = useParams<RouteParams>();
  const companyDetailsQuery = CompanyQueries.useCompanyDetails(Number(companyId));
  const demoFeaturesQuery = CustomerServiceQueries.useDemoFeaturesList();

  // check if base product is D&O Suite (and for BBI Survey in dev)
  const isDNOSuiteOrBBISurvey = ["D&O suite", "BBI Surveys"].map(x => x.toLowerCase()).includes(companyDetailsQuery?.data?.baseProduct?.product?.name?.toLowerCase() ?? ''); 
  const companyProductsQuery = ProductQueries.useCompanyProducts(companyDetailsQuery?.data?.id, companyDetailsQuery?.data?.deleted);
  const supportDetailsQuery = CompanyQueries.useSupportDetails(companyDetailsQuery?.data?.id, companyDetailsQuery?.data?.deleted);
  const companyDocumentSettingsQuery = CompanyQueries.useCompanyDocumentSettings(companyDetailsQuery?.data?.id, companyDetailsQuery?.data?.deleted || isDNOSuiteOrBBISurvey);
  const companyMeetingSettingsQuery = CompanyQueries.useCompanyMeetingSettings(companyDetailsQuery?.data?.id, companyDetailsQuery?.data?.deleted || isDNOSuiteOrBBISurvey);
  const companyLogoQuery = CompanyQueries.useCompanyLogo(companyDetailsQuery?.data?.id, companyDetailsQuery?.data?.deleted);
  const timeZoneQuery = TimeZoneQueries.useTimeZones();
  const usersQuery = UserQueries.useCompanyUsersSimple(companyDetailsQuery?.data?.id, companyDetailsQuery?.data?.deleted);
  const subscriptionQuery = SubscriptionQueries.useSubscription(companyDetailsQuery?.data?.id, companyDetailsQuery?.data?.deleted);
  const activityQuery = ActivityLogQueries.useCompanyActivity(companyDetailsQuery?.data?.id, companyDetailsQuery?.data?.deleted);
  const parentChildRelationsQuery = CompanyQueries.useCompanyParentChildRelations(companyDetailsQuery?.data?.id, companyDetailsQuery?.data?.deleted);

  const [currentTab, setCurrentTab] = useState(tab ?? "users");

  const history = useHistory();
  const validTabs = ["users","products","features","usage"]

  useEffect(() => {
    if(tab !== undefined && !validTabs.includes(tab)) {
      history.replace(`/admin/company/${companyId}`)
      setCurrentTab("users")
      return
    }

    if(tab !== currentTab && tab !== undefined) {
      setCurrentTab(tab)
    }
  },[tab])

  useEffect(() => {
    if(!window.location.href.includes(`${companyId}/${currentTab}`)) {
      if(tab === undefined && currentTab === "users") {
        return
      }
      history.push(`/admin/company/${companyId}/${currentTab}`)
    }
  },[currentTab])

  return (
    <Spin spinning={companyDetailsQuery.isLoading}>
      {companyDetailsQuery.isError ? (
        <Alert
          type='error'
          message='Unable to retrieve company details. Please verify the company is valid.'
          showIcon
        />
      ) : (
        <Row gutter={[24, 24]}>
          <Col span={24}>
            <CompanyAlert
              company={companyDetailsQuery.data}
              subscription={subscriptionQuery.data}
              isLoading={companyDetailsQuery.isLoading}
            />
          </Col>
          {!companyDetailsQuery?.data?.deleted && (
            <>
              <Col span={24}>
                <ActivityChartCard
                  companyActivity={activityQuery.data}
                  pageIsLoading={companyDetailsQuery?.isLoading}
                  chartIsLoading={
                    activityQuery.isLoading &&
                    !companyDetailsQuery.isLoading
                  }
                />
              </Col>
              <Col xs={24} md={14} lg={16}>
                <Card>
                  <Tabs
                    activeKey={currentTab ?? 'users'}
                    onChange={(tab) => setCurrentTab(tab)}
                  >
                    <TabPane tab='Users' key='users'>
                      <UsersTab
                        users={usersQuery.data}
                        isLoading={
                          !companyDetailsQuery.isLoading &&
                          usersQuery.isLoading
                        }
                        samlEnabledForCompany={companyDetailsQuery.data?.singleSignOnEnabled}
                        samlConfiguredForCompany={companyDetailsQuery.data?.singleSignOnConfigured}
                      />
                    </TabPane>
                    <TabPane tab='Products' key='products'>
                      <ProductsTab
                        products={companyProductsQuery.data}
                        isLoading={
                          !companyDetailsQuery.isLoading &&
                          companyProductsQuery.isLoading
                        }
                        demoCompany={companyDetailsQuery.data?.isDemoCompany ?? false}
                        demoFeatures={demoFeaturesQuery.data}
                        companyId={Number(companyId)}
                        companyName={companyDetailsQuery.data?.name ?? ''}
                        canUpdate={HasRight([Rights.CSAdmin])}
                      />
                    </TabPane>
                    <TabPane tab='Features' key='features'>
                      <CompanyFeaturesTab
                        demoCompany={companyDetailsQuery.data?.isDemoCompany ?? false}
                        products={companyProductsQuery.data}
                        demoFeatures={demoFeaturesQuery.data}
                        company={companyDetailsQuery.data}
                        isLoading={
                          !companyDetailsQuery.isLoading &&
                          companyProductsQuery.isLoading
                        }
                      />
                    </TabPane>
                    <TabPane tab='Usage' key='usage'>
                      <CompanyUsageTab 
                      company={companyDetailsQuery.data}
                      demoCompany={companyDetailsQuery.data?.isDemoCompany ?? false}
                      products={companyProductsQuery.data}
                      demoFeatures={demoFeaturesQuery.data}
                      isLoading={
                        !companyDetailsQuery.isLoading &&
                        companyProductsQuery.isLoading}
                      />
                    </TabPane>
                  </Tabs>
                </Card>
              </Col>
              <Col xs={24} md={10} lg={8}>
                <Space
                  direction='vertical'
                  size='large'
                  style={{ display: 'flex' }}
                >
                  <FeaturesCard
                    supportDetails={supportDetailsQuery.data}
                    companyDetails={companyDetailsQuery.data}
                    products={companyProductsQuery.data}
                    demoFeatures={demoFeaturesQuery.data}
                    companyDocumentSettings={companyDocumentSettingsQuery.data}
                    companyMeetingSettings={companyMeetingSettingsQuery.data}
                    companyLogo={companyLogoQuery.data}
                    timeZones={timeZoneQuery.data}
                    isLoading={
                      !companyDetailsQuery.isLoading &&
                      (supportDetailsQuery.isLoading ||
                        (!isDNOSuiteOrBBISurvey && companyDocumentSettingsQuery.isLoading) ||
                        (!isDNOSuiteOrBBISurvey && companyMeetingSettingsQuery.isLoading) ||
                        companyLogoQuery.isLoading ||
                        timeZoneQuery.isLoading ||
                        subscriptionQuery.isLoading)
                    }
                    canUpdate={HasRight([Rights.CSAdmin])}
                    subscription={subscriptionQuery.data}
                  />
                  <ParentChildrenCard
                    parentChildren={parentChildRelationsQuery.data}
                  />
                  <CompanyAddressCard company={companyDetailsQuery.data} />
                  <CompanyBillingAddressCard
                    subscription={subscriptionQuery.data}
                  />
                </Space>
              </Col>
            </>
          )}
        </Row>
      )}
    </Spin>
  );
}
